/**
 * BellIcon — Minimal line-art glyphs for each of the 16 synthesized instruments.
 *
 * Art direction:
 *   - Ink on parchment, no fills — strokes only except for the ember accent
 *   - `currentColor` for lines; `var(--rc-clay)` for the single strike-point accent
 *   - 48×48 viewBox, designed to render from 20px (list row) to 56px (preview center)
 *   - Each glyph is instantly recognisable as the physical instrument
 *
 * SOLID: single responsibility — maps BellInstrumentId → SVG. No state.
 * KISS: inline paths, no external assets, no runtime cost.
 */
import type { BellInstrumentId } from "@retreat/core";

/** Sizes used by the two call-sites — named here so components don't use bare literals. */
export const BELL_ICON_SIZE = {
  /** Center glyph inside BellPreviewScreen resonance rings */
  PREVIEW: 56,
  /** Row icon in BellLibraryScreen list */
  ROW: 28,
} as const;

type Props = {
  id: BellInstrumentId;
  size?: number;
  className?: string;
};

// One SVG fragment per implemented instrument — all paths within [3, 3, 45, 45] bounds.
// Stub/future instruments fall back to ICONS.rin in the render function.
const ICONS: Partial<Record<BellInstrumentId, React.ReactNode>> = {

  // ── rin (Meditation Bowl) — bowl seen from above ────────────────────────────
  rin: <>
    <circle cx="24" cy="24" r="18" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="24" cy="24" r="11" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.45"/>
    <circle cx="24" cy="24" r="3" fill="var(--rc-clay)"/>
  </>,

  // ── keisu (Altar Bowl) — wide bowl from the side, on a cushion ─────────────
  keisu: <>
    <ellipse cx="24" cy="24" rx="17" ry="10" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <ellipse cx="24" cy="24" rx="9" ry="5" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.45"/>
    <line x1="11" y1="34" x2="37" y2="34" stroke="currentColor" strokeWidth="1.8"/>
    <line x1="8" y1="37" x2="40" y2="37" stroke="currentColor" strokeWidth="1.2" opacity="0.45"/>
    <circle cx="24" cy="24" r="2.5" fill="var(--rc-clay)"/>
  </>,

  // ── inkin (Procession Bell) — small hand bell with loop ────────────────────
  inkin: <>
    <path d="M16 36 Q16 16 24 13 Q32 16 32 36" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    <line x1="14" y1="36" x2="34" y2="36" stroke="currentColor" strokeWidth="2"/>
    <line x1="24" y1="13" x2="24" y2="8" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="24" cy="7" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="24" cy="36" r="2" fill="var(--rc-clay)"/>
  </>,

  // ── ogane (Great Temple Bell) — large hanging bell ─────────────────────────
  ogane: <>
    <path d="M10 9 L10 33 Q10 44 24 44 Q38 44 38 33 L38 9" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <line x1="8" y1="9" x2="40" y2="9" stroke="currentColor" strokeWidth="2"/>
    <line x1="24" y1="4" x2="24" y2="9" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="24" cy="3.5" r="2" fill="var(--rc-clay)"/>
    <circle cx="24" cy="30" r="4" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.45"/>
  </>,

  // ── han (Life-and-Death Board) — flat wooden plank with strokes ─────────────
  han: <>
    <rect x="7" y="13" width="34" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="24" cy="24" r="6" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
    <circle cx="24" cy="24" r="1.5" fill="var(--rc-clay)"/>
    <line x1="7" y1="18" x2="41" y2="18" stroke="currentColor" strokeWidth="0.6" opacity="0.35"/>
    <line x1="7" y1="30" x2="41" y2="30" stroke="currentColor" strokeWidth="0.6" opacity="0.35"/>
  </>,

  // ── hyoshigi (Walking Clappers) — two crossed rectangular clappers ──────────
  hyoshigi: <>
    <rect x="6" y="20" width="36" height="8" rx="3" fill="none" stroke="currentColor" strokeWidth="1.4" transform="rotate(-30 24 24)"/>
    <rect x="6" y="20" width="36" height="8" rx="3" fill="none" stroke="currentColor" strokeWidth="1.4" transform="rotate(30 24 24)"/>
    <circle cx="24" cy="24" r="2" fill="var(--rc-clay)"/>
  </>,

  // ── mokugyo (Wooden Fish) — fish-shaped hollow wooden block ────────────────
  mokugyo: <>
    <ellipse cx="22" cy="24" rx="14" ry="9" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M36 24 L44 17 L44 31 Z" fill="none" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="16" cy="21" r="1.8" fill="none" stroke="currentColor" strokeWidth="1"/>
    <line x1="18" y1="24" x2="34" y2="24" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/>
    <circle cx="22" cy="24" r="2" fill="var(--rc-clay)"/>
  </>,

  // ── singing_bowl (Himalayan Singing Bowl) — round bowl from the side ────────
  singing_bowl: <>
    <path d="M8 28 Q8 10 24 10 Q40 10 40 28" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <line x1="6" y1="28" x2="42" y2="28" stroke="currentColor" strokeWidth="2"/>
    <ellipse cx="24" cy="28" rx="16" ry="5" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.4"/>
    <circle cx="24" cy="18" r="2.5" fill="var(--rc-clay)"/>
  </>,

  // ── tingsha (Clarity Cymbals) — two small cymbals joined by a leather cord ─
  tingsha: <>
    <circle cx="14" cy="32" r="8" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="34" cy="32" r="8" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M14 24 Q24 12 34 24" fill="none" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="24" cy="12" r="2" fill="none" stroke="currentColor" strokeWidth="1"/>
    <circle cx="14" cy="32" r="2" fill="var(--rc-clay)"/>
    <circle cx="34" cy="32" r="2" fill="var(--rc-clay)"/>
  </>,

  // ── drilbu (Tibetan Ritual Bell) — bell with dorje crown ───────────────────
  drilbu: <>
    <path d="M16 38 Q16 22 24 19 Q32 22 32 38" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    <line x1="14" y1="38" x2="34" y2="38" stroke="currentColor" strokeWidth="2"/>
    <circle cx="24" cy="19" r="4" fill="none" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="24" y1="15" x2="24" y2="10" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="20" y1="9" x2="28" y2="9" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="21" y1="7" x2="27" y2="7" stroke="currentColor" strokeWidth="1"/>
    <circle cx="24" cy="9" r="1.5" fill="var(--rc-clay)"/>
  </>,

  // ── damaru (Double Drum) — hourglass-shaped Tibetan hand drum ───────────────
  damaru: <>
    <line x1="10" y1="10" x2="38" y2="10" stroke="currentColor" strokeWidth="2"/>
    <line x1="10" y1="38" x2="38" y2="38" stroke="currentColor" strokeWidth="2"/>
    <path d="M10 10 Q24 24 10 38" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M38 10 Q24 24 38 38" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="24" cy="24" r="2.5" fill="var(--rc-clay)"/>
    <line x1="22" y1="10" x2="26" y2="38" stroke="currentColor" strokeWidth="0.7" opacity="0.35" strokeDasharray="2 2"/>
  </>,

  // ── yunqing (Cloud Gong) — flat round gong hanging from a cord ─────────────
  yunqing: <>
    <circle cx="24" cy="26" r="17" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="24" cy="26" r="9" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.45"/>
    <circle cx="24" cy="26" r="2" fill="var(--rc-clay)"/>
    <line x1="24" y1="9" x2="24" y2="5" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="20" y1="5" x2="28" y2="5" stroke="currentColor" strokeWidth="1.2"/>
  </>,

  // ── zhong (Chinese Temple Bell) — large bronze bell with band ──────────────
  zhong: <>
    <path d="M11 8 L11 33 Q11 44 24 44 Q37 44 37 33 L37 8" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <line x1="9" y1="8" x2="39" y2="8" stroke="currentColor" strokeWidth="2"/>
    <line x1="24" y1="4" x2="24" y2="8" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="24" cy="3.5" r="2" fill="var(--rc-clay)"/>
    <line x1="11" y1="20" x2="37" y2="20" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
    <circle cx="24" cy="32" r="3.5" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.45"/>
  </>,

  // ── moktak (Wooden Block) — rounded wooden block with hollow slit ────────────
  moktak: <>
    <rect x="8" y="16" width="32" height="18" rx="9" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <line x1="12" y1="25" x2="36" y2="25" stroke="currentColor" strokeWidth="1" opacity="0.4"/>
    <circle cx="24" cy="25" r="2.5" fill="var(--rc-clay)"/>
  </>,

  // ── beomjong (Korean Dharma Bell) — large bell with dragon loop ─────────────
  beomjong: <>
    <path d="M11 9 L11 33 Q11 44 24 44 Q37 44 37 33 L37 9" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <line x1="9" y1="9" x2="39" y2="9" stroke="currentColor" strokeWidth="2"/>
    <ellipse cx="24" cy="5" rx="5" ry="3.5" fill="none" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="24" cy="5" r="1.5" fill="var(--rc-clay)"/>
    <line x1="11" y1="19" x2="37" y2="19" stroke="currentColor" strokeWidth="0.7" opacity="0.5"/>
    <line x1="11" y1="25" x2="37" y2="25" stroke="currentColor" strokeWidth="0.5" opacity="0.3"/>
  </>,

  // ── vipassana_gong (Goenka Gong) — large flat gong on a stand ───────────────
  vipassana_gong: <>
    <circle cx="24" cy="22" r="17" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="24" cy="22" r="9" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.45"/>
    <circle cx="24" cy="22" r="3" fill="var(--rc-clay)"/>
    <line x1="14" y1="39" x2="34" y2="39" stroke="currentColor" strokeWidth="2"/>
    <line x1="17" y1="39" x2="17" y2="35" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="31" y1="39" x2="31" y2="35" stroke="currentColor" strokeWidth="1.5"/>
  </>,
};

export function BellIcon({ id, size = 40, className }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      {ICONS[id] ?? ICONS.rin}
    </svg>
  );
}
